/**
 * Cross-shell confirm/alert (AB#505, AB#650 follow-up).
 *
 * Tauri v2's WKWebView does NOT render native `window.confirm`/`window.alert`
 * (they return falsy / no-op), and native `window.confirm` can't be accepted by
 * browser automation either. `confirmDialog` therefore renders the in-app
 * `<Dialog>` via `<ConfirmHost>` (mounted once in `__root.tsx`); it falls back
 * to the native dialogs only when no host is mounted.
 *
 * Both are async; callers must `await`.
 */
import { isTauri } from '@wordpuppi/shared';

/**
 * `destructive` = the confirm loses data or breaks something for others: the confirm
 * button turns red, names the action (`confirmLabel`, e.g. "Delete site", never "OK"),
 * and focus starts on Cancel so a stray Enter is safe (docs/style/ui-details.md).
 */
export type ConfirmOptions = { title?: string; confirmLabel?: string; cancelLabel?: string; destructive?: boolean };
export type ConfirmRequest = ConfirmOptions & { message: string; resolve: (ok: boolean) => void };

let pending: ConfirmRequest | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** Store for `<ConfirmHost>` — module-level so `confirmDialog` needs no React context. */
export const confirmStore = {
  subscribe(l: () => void) {
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  },
  get: () => pending,
  settle(ok: boolean) {
    const p = pending;
    pending = null;
    emit();
    p?.resolve(ok);
  },
};

/** OK/Cancel confirmation → true when the user accepts. */
export function confirmDialog(message: string, opts?: ConfirmOptions): Promise<boolean> {
  if (listeners.size === 0) return Promise.resolve(window.confirm(message)); // ponytail: host not mounted (tests)
  return new Promise((resolve) => {
    pending?.resolve(false);
    pending = { ...opts, message, resolve };
    emit();
  });
}

/** Informational dialog with a single dismiss button. */
export async function alertDialog(message: string, opts?: { title?: string }): Promise<void> {
  if (isTauri()) {
    const { message: showMessage } = await import('@tauri-apps/plugin-dialog');
    await showMessage(message, opts?.title ? { title: opts.title } : undefined);
    return;
  }
  window.alert(message);
}
